import React from "react";
import { Form, Typography, message } from "antd";
import PageLoader from "./PageLoader";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import UserForm from "./UserForm";
import { useCurrentUser } from "../lib/useCurrentUser";

const { Title } = Typography;

const USER_QUERY = gql`
  query UserQuery($userId: ID!) {
    user(id: $userId) {
      id
      firstName
      lastName
      email
      calendarLink
      active
      permissions {
        id
      }
    }
  }
`;

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser(
    $id: ID!
    $firstName: String
    $lastName: String
    $calendarLink: String
    $active: Boolean
    $permissionIds: [String!]
  ) {
    updateUser(
      id: $id
      firstName: $firstName
      lastName: $lastName
      calendarLink: $calendarLink
      active: $active
      permissionIds: $permissionIds
    ) {
      id
      firstName
      lastName
      email
      calendarLink
      active
      permissions {
        id
        description
      }
    }
  }
`;

const UserEdit: React.FC = () => {
  const { user_id } = useParams<{ user_id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const {
    currentUser,
    hasPermission,
    loading: currentUserLoading,
  } = useCurrentUser();
  const { data, loading, error } = useQuery(USER_QUERY, {
    variables: { userId: user_id },
    onCompleted: (data) => {
      if (data?.user) {
        form.setFieldsValue({
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.email,
          calendarLink: data.user.calendarLink ?? "",
          active: data.user.active,
          permissionIds: data.user.permissions.map((p: any) => p.id),
        });
      }
    },
  });

  const [updateUser, { loading: saving }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: (result) => {
      // If users deactivate their own account redirect to the login screen.
      const updated = result?.updateUser;
      if (
        updated &&
        currentUser &&
        String(updated.id) === String(currentUser.id) &&
        updated.active === false
      ) {
        window.location.reload();
        return;
      }
      messageApi.success("User updated successfully");
    },
    onError: (err) => {
      messageApi.error(err.message);
    },
  });

  if (currentUserLoading || loading)
    return <PageLoader style={{ display: "block", marginTop: 40 }} />;
  if (error) return <p>Error: {error.message}</p>;
  if (!data?.user) return <Navigate to="/users" replace />;
  if (!hasPermission("users.manage")) return <Navigate to="/users" replace />;

  const user = data.user;

  const onFinish = (values: any) => {
    updateUser({
      variables: {
        id: user_id,
        firstName: values.firstName,
        lastName: values.lastName,
        calendarLink: values.calendarLink || null,
        active: values.active,
        permissionIds: values.permissionIds,
      },
    });
  };

  return (
    <div style={{ maxWidth: 600 }}>
      {contextHolder}
      <Title level={3} style={{ marginBottom: 24 }}>
        {user.firstName} {user.lastName}
      </Title>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <UserForm
          form={form}
          saving={saving}
          emailLocked
          submitLabel="Save"
          onCancel={() => navigate("/users")}
        />
      </Form>
    </div>
  );
};

export default UserEdit;
